/* eslint-disable react-refresh/only-export-components */
import React from "react";

import axios from "axios";
import { useNavigate } from "react-router-dom";

import { useAuthStore } from "@/features/auth/store/authStore";

import { INNOVATION_ROUTES } from "../../../routes";
import {
  buildExperimentPayload,
  getExperimentApiErrorMessage,
  getExperimentOrchestrationResult,
  getExperimentOrchestrationStatus,
  startExperimentOrchestration,
  useCreateExperimentMutation,
  useExperimentByIdQuery,
  useUpdateExperimentMutation,
} from "../api/experimentsApi";
import { getExperimentStepKeyByIndex, getExperimentStepPath } from "../routes";
import {
  initialExperimentFormData,
  mapExperimentDetailToFormData,
  serializeContextStep,
  serializeResearchStep,
} from "../utils/experimentEditorForm";
import {
  isExperimentLockedStatus,
  normalizeExperimentStatus,
} from "../utils/experimentStatus";
import { validateContextStep } from "../validations/contextValidation";
import { validateResearchStep } from "../validations/researchValidation";

import type {
  OrchestrationErrorData,
  OrchestrationResultData,
  OrchestrationStatusData,
} from "../api/experimentsApi";
import type { ExperimentFormData } from "../types/experiment.types";
import type {
  ExperimentEditorContextValue,
  ExperimentFlowStepKey,
} from "../types/experimentFlow.types";

declare global {
  interface Window {
    validateCurrentStep?: () => { isValid: boolean; missingFields: string[] };
  }
}

const ExperimentEditorContext =
  React.createContext<ExperimentEditorContextValue | null>(null);

const resolveApiErrorMessage = (error: unknown, fallback: string) => {
  if (!axios.isAxiosError(error)) {
    return error instanceof Error ? error.message : fallback;
  }

  const responseData = error.response?.data as
    | { errors?: string[]; message?: string }
    | undefined;

  return (
    responseData?.errors?.join(", ") ||
    responseData?.message ||
    error.message ||
    fallback
  );
};

const extractOrchestrationPlan = (
  data: OrchestrationResultData | null,
): string | null => {
  if (!data) return null;

  if (typeof data.plan === "string" && data.plan.trim()) {
    return data.plan.trim();
  }
  if (typeof data.summaryPlan === "string" && data.summaryPlan.trim()) {
    return data.summaryPlan.trim();
  }
  if (
    typeof data.implementationPlan === "string" &&
    data.implementationPlan.trim()
  ) {
    return data.implementationPlan.trim();
  }

  const record = data.v0PromptRecord as
    | Record<string, unknown>
    | null
    | undefined;
  const recordPlan =
    record?.implementationPlan ??
    record?.summaryPlan ??
    record?.plan ??
    record?.prompt;

  if (typeof recordPlan === "string" && recordPlan.trim()) {
    return recordPlan.trim();
  }

  return null;
};

const isOrchestrationComplete = (status?: OrchestrationStatusData | null) => {
  if (!status) return false;

  const progress = typeof status.progress === "number" ? status.progress : null;
  if (progress !== null && progress >= 100) {
    return true;
  }

  const rawStatus = (status.status ?? status.currentStage ?? "")
    .toString()
    .trim()
    .toLowerCase();
  if (["completed", "complete", "finished", "done"].includes(rawStatus)) {
    return true;
  }

  const metadata = status.metadata as
    | Record<string, unknown>
    | null
    | undefined;
  if (
    metadata &&
    typeof metadata.completedAt === "string" &&
    metadata.completedAt.trim()
  ) {
    return true;
  }

  return false;
};

const extractOrchestrationErrorMessage = (
  status?: OrchestrationStatusData | null,
): string | null => {
  const error = status?.error;

  if (!error) {
    return null;
  }

  if (typeof error === "string") {
    return error.trim() || null;
  }

  const typedError = error as OrchestrationErrorData;
  const parts = [
    typedError.message?.trim(),
    typedError.failedStage?.trim()
      ? `Stage: ${typedError.failedStage.trim()}`
      : null,
    typedError.details?.trim(),
  ].filter(Boolean);

  return parts.length > 0 ? parts.join(" ") : null;
};

const isOrchestrationFailed = (status?: OrchestrationStatusData | null) => {
  const rawStatus = (status?.status ?? status?.currentStage ?? "")
    .toString()
    .trim()
    .toLowerCase();

  return rawStatus === "failed";
};

const ORCHESTRATION_PENDING_STORAGE_KEY =
  "falcon-innovation-studio:orchestration-pending";
const ORCHESTRATION_SESSION_STORAGE_KEY =
  "falcon-innovation-studio:orchestration-session";

const getOrchestrationPendingStorageKey = (experimentId: string) =>
  `${ORCHESTRATION_PENDING_STORAGE_KEY}:${experimentId}`;
const getOrchestrationSessionStorageKey = (experimentId: string) =>
  `${ORCHESTRATION_SESSION_STORAGE_KEY}:${experimentId}`;

const readOrchestrationPendingFlag = (experimentId?: string) => {
  if (!experimentId || typeof window === "undefined") {
    return false;
  }

  try {
    return (
      window.localStorage.getItem(
        getOrchestrationPendingStorageKey(experimentId),
      ) === "true"
    );
  } catch {
    return false;
  }
};

const writeOrchestrationPendingFlag = (
  experimentId: string | undefined,
  isPending: boolean,
) => {
  if (!experimentId || typeof window === "undefined") {
    return;
  }

  try {
    const storageKey = getOrchestrationPendingStorageKey(experimentId);
    if (isPending) {
      window.localStorage.setItem(storageKey, "true");
      return;
    }

    window.localStorage.removeItem(storageKey);
  } catch {
    // Ignore storage failures and fall back to in-memory state.
  }
};

const readStoredOrchestrationId = (experimentId?: string) => {
  if (!experimentId || typeof window === "undefined") {
    return null;
  }

  try {
    return (
      window.localStorage.getItem(
        getOrchestrationSessionStorageKey(experimentId),
      ) || null
    );
  } catch {
    return null;
  }
};

const writeStoredOrchestrationId = (
  experimentId: string | undefined,
  orchestrationId: string | null,
) => {
  if (!experimentId || typeof window === "undefined") {
    return;
  }

  try {
    const storageKey = getOrchestrationSessionStorageKey(experimentId);
    if (orchestrationId) {
      window.localStorage.setItem(storageKey, orchestrationId);
      return;
    }

    window.localStorage.removeItem(storageKey);
  } catch {
    // Ignore storage failures and fall back to in-memory state.
  }
};

interface ExperimentEditorProviderProps {
  experimentId?: string;
  children: React.ReactNode;
}

export const ExperimentEditorProvider: React.FC<
  ExperimentEditorProviderProps
> = ({ experimentId, children }) => {
  const navigate = useNavigate();
  const authUser = useAuthStore((state) => state.user);
  const createExperimentMutation = useCreateExperimentMutation();
  const updateExperimentMutation = useUpdateExperimentMutation();
  const { data: selectedExperiment, isLoading: isExperimentLoading } =
    useExperimentByIdQuery(experimentId);

  const hydratedExperimentData = React.useMemo(
    () =>
      selectedExperiment
        ? mapExperimentDetailToFormData(selectedExperiment)
        : initialExperimentFormData,
    [selectedExperiment],
  );
  const experimentStatus = React.useMemo(
    () => normalizeExperimentStatus(selectedExperiment?.status),
    [selectedExperiment?.status],
  );
  const isExperimentLocked = React.useMemo(
    () => isExperimentLockedStatus(selectedExperiment?.status),
    [selectedExperiment?.status],
  );
  const isExperimentViewOnly = isExperimentLocked;

  const [experimentDataOverride, setExperimentDataOverride] =
    React.useState<ExperimentFormData | null>(null);
  const experimentData = experimentDataOverride ?? hydratedExperimentData;
  const [isFinalized, setIsFinalized] = React.useState(false);
  const [submissionError, setSubmissionError] = React.useState<string | null>(
    null,
  );
  const [contextBaselineOverride, setContextBaselineOverride] = React.useState<
    string | null
  >(null);
  const [researchBaselineOverride, setResearchBaselineOverride] =
    React.useState<string | null>(null);
  const [orchestrationId, setOrchestrationId] = React.useState<string | null>(
    null,
  );
  const [orchestrationStatus, setOrchestrationStatus] =
    React.useState<OrchestrationStatusData | null>(null);
  const [orchestrationResult, setOrchestrationResult] =
    React.useState<OrchestrationResultData | null>(null);
  const [orchestrationPlan, setOrchestrationPlan] = React.useState<
    string | null
  >(null);
  const [orchestrationProgress, setOrchestrationProgress] = React.useState<
    number | null
  >(null);
  const [orchestrationError, setOrchestrationError] = React.useState<
    string | null
  >(null);
  const [isOrchestrationLoading, setIsOrchestrationLoading] =
    React.useState(false);
  const [hasPendingOrchestrationChanges, setHasPendingOrchestrationChanges] =
    React.useState(false);
  const orchestrationPollRef = React.useRef<number | null>(null);

  React.useEffect(() => {
    const resolvedExperimentId =
      hydratedExperimentData.experimentId ?? experimentId;
    const pendingChanges = readOrchestrationPendingFlag(resolvedExperimentId);
    setExperimentDataOverride(null);
    setIsFinalized(false);
    setSubmissionError(null);
    setContextBaselineOverride(null);
    setResearchBaselineOverride(null);
    setOrchestrationId(
      pendingChanges ? null : readStoredOrchestrationId(resolvedExperimentId),
    );
    setOrchestrationStatus(null);
    setOrchestrationResult(null);
    setOrchestrationPlan(null);
    setOrchestrationProgress(null);
    setOrchestrationError(null);
    setIsOrchestrationLoading(false);
    setHasPendingOrchestrationChanges(pendingChanges);
    if (orchestrationPollRef.current) {
      window.clearInterval(orchestrationPollRef.current);
      orchestrationPollRef.current = null;
    }
  }, [experimentId, hydratedExperimentData.experimentId]);

  const setExperimentData = React.useCallback<
    React.Dispatch<React.SetStateAction<ExperimentFormData>>
  >(
    (value) => {
      setExperimentDataOverride((previous) => {
        const base = previous ?? hydratedExperimentData;
        return typeof value === "function"
          ? (value as (current: ExperimentFormData) => ExperimentFormData)(base)
          : value;
      });
    },
    [hydratedExperimentData],
  );

  const contextBaseline =
    contextBaselineOverride ?? serializeContextStep(hydratedExperimentData);
  const researchBaseline =
    researchBaselineOverride ?? serializeResearchStep(hydratedExperimentData);

  const isContextDirty = React.useMemo(
    () => serializeContextStep(experimentData) !== contextBaseline,
    [contextBaseline, experimentData],
  );
  const isResearchDirty = React.useMemo(
    () => serializeResearchStep(experimentData) !== researchBaseline,
    [experimentData, researchBaseline],
  );

  const buildPayload = React.useCallback(() => {
    return buildExperimentPayload(experimentData, {
      userId: authUser?.id,
      owner: authUser
        ? `${authUser.firstName} ${authUser.lastName}`.trim()
        : undefined,
    });
  }, [authUser, experimentData]);

  const clearSubmissionError = React.useCallback(() => {
    setSubmissionError(null);
  }, []);

  const saveContextStep = React.useCallback(async () => {
    const existingExperimentId = experimentData.experimentId ?? experimentId;
    if (isExperimentViewOnly) {
      setSubmissionError(null);
      return existingExperimentId;
    }

    if (!isContextDirty && existingExperimentId) {
      setSubmissionError(null);
      return existingExperimentId;
    }

    const validation = validateContextStep({
      name: experimentData.name,
      goal: experimentData.goal,
      description: experimentData.description,
    });
    if (!validation.isValid) {
      return undefined;
    }

    const payload = buildPayload();

    if (!payload.name || !payload.goal || !payload.context) {
      setSubmissionError(
        "Please complete experiment name, business goal, and context.",
      );
      return undefined;
    }

    try {
      setSubmissionError(null);

      let resolvedExperimentId = existingExperimentId;

      if (resolvedExperimentId) {
        await updateExperimentMutation.mutateAsync({
          experimentId: resolvedExperimentId,
          payload,
        });
      } else {
        const response = await createExperimentMutation.mutateAsync(payload);
        resolvedExperimentId =
          response?.data?.id ??
          response?.data?.experimentId ??
          response?.metadata?.experimentId ??
          response?.id ??
          response?.experimentId;
      }

      setExperimentData((previous) => ({
        ...previous,
        experimentId: resolvedExperimentId ?? previous.experimentId,
        supportingDocFiles: [],
      }));
      const finalExperimentId =
        resolvedExperimentId ?? experimentData.experimentId ?? experimentId;
      setHasPendingOrchestrationChanges(true);
      writeOrchestrationPendingFlag(finalExperimentId, true);
      writeStoredOrchestrationId(finalExperimentId, null);
      setOrchestrationId(null);
      setOrchestrationStatus(null);
      setOrchestrationResult(null);
      setOrchestrationPlan(null);
      setOrchestrationProgress(null);
      setOrchestrationError(null);
      setContextBaselineOverride(
        serializeContextStep({
          ...experimentData,
          experimentId: resolvedExperimentId ?? experimentData.experimentId,
          supportingDocFiles: [],
        }),
      );

      return resolvedExperimentId ?? experimentData.experimentId;
    } catch (error) {
      setSubmissionError(
        resolveApiErrorMessage(error, "Failed to save experiment."),
      );
      return undefined;
    }
  }, [
    buildPayload,
    createExperimentMutation,
    experimentData,
    experimentId,
    isExperimentViewOnly,
    isContextDirty,
    setExperimentData,
    updateExperimentMutation,
  ]);

  const saveResearchStep = React.useCallback(async () => {
    const resolvedExperimentId = experimentData.experimentId ?? experimentId;

    if (isExperimentViewOnly) {
      setSubmissionError(null);
      return Boolean(resolvedExperimentId);
    }

    const researchValidation = validateResearchStep({
      researchObjective: experimentData.researchObjective,
      primaryHypothesis: experimentData.primaryHypothesis,
    });
    if (!researchValidation.isValid) {
      return false;
    }

    if (!resolvedExperimentId) {
      setSubmissionError(
        "Experiment ID missing. Save the context step before continuing.",
      );
      return false;
    }

    if (!isResearchDirty) {
      setSubmissionError(null);
      return true;
    }

    try {
      setSubmissionError(null);
      await updateExperimentMutation.mutateAsync({
        experimentId: resolvedExperimentId,
        payload: buildPayload(),
      });
      setExperimentData((previous) => ({
        ...previous,
        supportingDocFiles: [],
      }));
      setHasPendingOrchestrationChanges(true);
      writeOrchestrationPendingFlag(resolvedExperimentId, true);
      writeStoredOrchestrationId(resolvedExperimentId, null);
      setOrchestrationId(null);
      setOrchestrationStatus(null);
      setOrchestrationResult(null);
      setOrchestrationPlan(null);
      setOrchestrationProgress(null);
      setOrchestrationError(null);
      setResearchBaselineOverride(
        serializeResearchStep({
          ...experimentData,
          experimentId: resolvedExperimentId,
          supportingDocFiles: [],
        }),
      );
      return true;
    } catch (error) {
      setSubmissionError(
        resolveApiErrorMessage(error, "Failed to update experiment."),
      );
      return false;
    }
  }, [
    buildPayload,
    experimentData,
    experimentId,
    isExperimentViewOnly,
    isResearchDirty,
    setExperimentData,
    updateExperimentMutation,
  ]);

  const runOrchestration = React.useCallback(
    async (
      targetExperimentId: string,
      options?: { force?: boolean },
    ) => {
      if (!targetExperimentId) return;
      if (isExperimentViewOnly && options?.force !== true) {
        return;
      }

      const shouldForce = options?.force === true;
      if (
        !shouldForce &&
        !readOrchestrationPendingFlag(targetExperimentId) &&
        !hasPendingOrchestrationChanges
      ) {
        return;
      }

      setIsOrchestrationLoading(true);
      setOrchestrationError(null);
      setOrchestrationPlan(null);

      try {
        const startResponse =
          await startExperimentOrchestration(targetExperimentId);
        const startId =
          startResponse.data?.orchestrationId ??
          (startResponse.data?.statusUrl ?? "").split("/").pop() ??
          null;

        if (!startId) {
          throw new Error("Missing orchestration id.");
        }

        setOrchestrationId(startId);
        setHasPendingOrchestrationChanges(false);
        writeOrchestrationPendingFlag(targetExperimentId, false);
        writeStoredOrchestrationId(targetExperimentId, startId);

        const statusResponse = await getExperimentOrchestrationStatus(
          targetExperimentId,
          startId,
        );

        if (statusResponse) {
          setOrchestrationStatus(statusResponse);
          setOrchestrationProgress(
            typeof statusResponse.progress === "number"
              ? statusResponse.progress
              : null,
          );
          setOrchestrationError(extractOrchestrationErrorMessage(statusResponse));
        }

        if (isOrchestrationComplete(statusResponse)) {
          const resultResponse = await getExperimentOrchestrationResult(
            targetExperimentId,
            startId,
          );

          if (resultResponse) {
            setOrchestrationResult(resultResponse);
            setOrchestrationProgress((prev) =>
              typeof resultResponse.progress === "number"
                ? resultResponse.progress
                : prev,
            );
            setOrchestrationStatus((prev) => ({
              ...(prev ?? {}),
              ...resultResponse,
            }));
            const resolvedPlan = extractOrchestrationPlan(resultResponse);
            if (resolvedPlan) {
              setOrchestrationPlan(resolvedPlan);
            }
          }
        }
      } catch (error) {
        setOrchestrationError(
          getExperimentApiErrorMessage(
            error,
            "Failed to orchestrate experiment.",
          ),
        );
      } finally {
        setIsOrchestrationLoading(false);
      }
    },
    [hasPendingOrchestrationChanges, isExperimentViewOnly],
  );

  React.useEffect(() => {
    if (!experimentId || !orchestrationId) {
      return;
    }

    if (orchestrationPollRef.current) {
      window.clearInterval(orchestrationPollRef.current);
      orchestrationPollRef.current = null;
    }

    const poll = async () => {
      try {
        const statusResponse = await getExperimentOrchestrationStatus(
          experimentId,
          orchestrationId,
        );

        if (statusResponse) {
          setOrchestrationStatus(statusResponse);
          setOrchestrationProgress(
            typeof statusResponse.progress === "number"
              ? statusResponse.progress
              : null,
          );
          setOrchestrationError(extractOrchestrationErrorMessage(statusResponse));

          if (isOrchestrationComplete(statusResponse)) {
            const resultResponse = await getExperimentOrchestrationResult(
              experimentId,
              orchestrationId,
            );

            if (resultResponse) {
              setOrchestrationResult(resultResponse);
              setOrchestrationProgress((prev) =>
                typeof resultResponse.progress === "number"
                  ? resultResponse.progress
                  : prev,
              );
              setOrchestrationStatus((prev) => ({
                ...(prev ?? {}),
                ...resultResponse,
              }));
              const resolvedPlan = extractOrchestrationPlan(resultResponse);
              if (resolvedPlan) {
                setOrchestrationPlan(resolvedPlan);
              }
            }

            if (orchestrationPollRef.current) {
              window.clearInterval(orchestrationPollRef.current);
              orchestrationPollRef.current = null;
            }
          } else if (isOrchestrationFailed(statusResponse)) {
            if (orchestrationPollRef.current) {
              window.clearInterval(orchestrationPollRef.current);
              orchestrationPollRef.current = null;
            }
          }
        }
      } catch (error) {
        setOrchestrationError(
          getExperimentApiErrorMessage(
            error,
            "Failed to refresh orchestration.",
          ),
        );
      }
    };

    void poll();

    orchestrationPollRef.current = window.setInterval(() => {
      void poll();
    }, 3000);

    return () => {
      if (orchestrationPollRef.current) {
        window.clearInterval(orchestrationPollRef.current);
        orchestrationPollRef.current = null;
      }
    };
  }, [experimentId, orchestrationId]);

  const navigateToStep = React.useCallback(
    (stepKey: ExperimentFlowStepKey, targetExperimentId?: string) => {
      const resolvedExperimentId =
        targetExperimentId ?? experimentData.experimentId ?? experimentId;

      if (stepKey !== "context" && !resolvedExperimentId) {
        setSubmissionError("Save the context step before continuing.");
        return;
      }

      navigate(getExperimentStepPath(stepKey, resolvedExperimentId));
    },
    [experimentData.experimentId, experimentId, navigate],
  );

  const goToStepByIndex = React.useCallback(
    (stepIndex: number) => {
      const stepKey = getExperimentStepKeyByIndex(stepIndex);
      if (!stepKey) {
        return;
      }

      navigateToStep(stepKey);
    },
    [navigateToStep],
  );

  const canAccessStep = React.useCallback(
    (stepKey: ExperimentFlowStepKey) => {
      if (stepKey === "context") {
        return true;
      }

      return Boolean(experimentData.experimentId ?? experimentId);
    },
    [experimentData.experimentId, experimentId],
  );

  const saveAndExit = React.useCallback(() => {
    navigate(INNOVATION_ROUTES.HOME);
  }, [navigate]);

  const contextValue = React.useMemo<ExperimentEditorContextValue>(
    () => ({
      experimentId: experimentData.experimentId ?? experimentId,
      experimentData,
      setExperimentData,
      selectedExperiment,
      experimentStatus,
      isLoading: Boolean(experimentId) && isExperimentLoading,
      isCreatingExperiment: createExperimentMutation.isPending,
      isUpdatingExperiment: updateExperimentMutation.isPending,
      isFinalized,
      isExperimentLocked,
      isExperimentViewOnly,
      setIsFinalized,
      submissionError,
      clearSubmissionError,
      saveContextStep,
      saveResearchStep,
      runOrchestration,
      hasPendingOrchestrationChanges,
      orchestrationId,
      orchestrationStatus,
      orchestrationResult,
      orchestrationPlan,
      orchestrationProgress,
      orchestrationError,
      isOrchestrationLoading,
      saveAndExit,
      navigateToStep,
      goToStepByIndex,
      canAccessStep,
    }),
    [
      canAccessStep,
      clearSubmissionError,
      createExperimentMutation.isPending,
      experimentData,
      experimentStatus,
      experimentId,
      goToStepByIndex,
      hasPendingOrchestrationChanges,
      isExperimentLoading,
      isFinalized,
      isExperimentLocked,
      isExperimentViewOnly,
      navigateToStep,
      orchestrationError,
      orchestrationId,
      orchestrationPlan,
      orchestrationProgress,
      orchestrationResult,
      orchestrationStatus,
      runOrchestration,
      isOrchestrationLoading,
      saveAndExit,
      saveContextStep,
      saveResearchStep,
      selectedExperiment,
      setExperimentData,
      submissionError,
      updateExperimentMutation.isPending,
    ],
  );

  return (
    <ExperimentEditorContext.Provider value={contextValue}>
      {children}
    </ExperimentEditorContext.Provider>
  );
};

export const useExperimentEditorContext = () => {
  const context = React.useContext(ExperimentEditorContext);

  if (!context) {
    throw new Error(
      "useExperimentEditorContext must be used within an ExperimentEditorProvider.",
    );
  }

  return context;
};
